<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { usersMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page: { title: i18n.t("label.createX", [i18n.t("module.user")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.createX", [i18n.t("module.user")]),
      items: [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.user"), href: "/users" },
        { text: i18n.t("label.createX", [i18n.t("module.user")]), active: true },
      ],
      form: { name: "", email: "", roleUuid: "", agentCode: "" },
      submitting: false,
      selectedRole: null,
      selectedAgent: null,
    };
  },
  validations: {
    form: {
      name: { required },
      email: { required, email },
      roleUuid: { required },
    },
  },
  computed: {
    ...mapState(LOOKUP_MODULE, ["roleDdl", "unlinkedAgentDdl"]),
  },
  async created() {
    await Promise.all([this.lookupRole(), this.lookupUnlinkedAgent({})]);
  },
  methods: {
    ...usersMethods,
    ...lookupMethods,
    onRoleChange(r) {
      this.selectedRole = r;
      this.form.roleUuid = r ? r.value : "";
    },
    onAgentChange(a) {
      this.selectedAgent = a;
      this.form.agentCode = a ? a.agent_code : "";
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.submitting = true;
      try {
        const result = await this.createUsers({ data: this.form, filter: {} });
        // Store action returns undefined on error (caught + alerted internally),
        // so only navigate when we got a successful response.
        if (result) this.$router.push({ name: "Users-listing" });
      } finally {
        this.submitting = false;
      }
    },
    cancel() {
      this.$router.push({ name: "Users-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.name')" label-for="name">
                    <b-form-input id="name" v-model="form.name" :state="$v.form.name.$dirty ? !$v.form.name.$error : null" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.email')" label-for="email">
                    <b-form-input id="email" type="email" v-model="form.email" :state="$v.form.email.$dirty ? !$v.form.email.$error : null" />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.role')" label-for="role">
                    <multiselect
                      id="role"
                      :value="selectedRole"
                      :options="roleDdl"
                      track-by="value"
                      label="name"
                      :show-labels="false"
                      :placeholder="$t('label.select')"
                      @input="onRoleChange"
                    />
                    <small v-if="$v.form.roleUuid.$dirty && $v.form.roleUuid.$error" class="text-danger">
                      {{ $t('label.role') }} is required
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.agentCode')" label-for="agent">
                    <multiselect
                      id="agent"
                      :value="selectedAgent"
                      :options="unlinkedAgentDdl"
                      track-by="agent_code"
                      :custom-label="(a) => `${a.agent_code} — ${a.agent_name || ''}`"
                      :show-labels="false"
                      :allow-empty="true"
                      :placeholder="'Select agent (only unlinked shown)'"
                      @input="onAgentChange"
                    />
                    <small class="text-muted">Only agents not yet linked to any user are listed.</small>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="AutoCount Location">
                    <div class="text-muted small">
                      <i class="mdi mdi-information-outline mr-1"></i>
                      Auto-generated as <code>AGT&lt;user_id&gt;</code> when an agent is linked. DO from this user will deduct stock from this location.
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="text-right">
                  <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                  <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
                </b-col>
              </b-row>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
